import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Command } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, PlusCircle, Clock, Eye, Shield, Building2,
  User, Moon, Sun, Globe, LogOut, FileText, Search,
  CheckCircle, AlertTriangle, XCircle, Loader2, BookOpen,
} from 'lucide-react'
import { assessmentsApi, authApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { verdictLabel } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useEntitlements } from '@/hooks/use-entitlements'
import i18n from '@/lib/i18n'

const VERDICT_ICONS: Record<string, JSX.Element> = {
  VERDE: <CheckCircle className="w-4 h-4 text-verde" />,
  GIALLO: <AlertTriangle className="w-4 h-4 text-giallo" />,
  ROSSO: <XCircle className="w-4 h-4 text-rosso" />,
  VIETATO: <XCircle className="w-4 h-4 text-rosso-dark" />,
}

export default function CommandPalette() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { user, logout, toggleDarkMode, language, setLanguage } = useAuthStore()
  const { toast } = useToast()

  // Global ⌘K / Ctrl+K
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const isAdmin = user?.role === 'PLATFORM_ADMIN'
  const isIspettore = user?.role === 'ISPETTORE'
  const isDpo = user?.role === 'DPO'
  const { data: entitlements } = useEntitlements()
  const historyLabel = entitlements && entitlements.plan !== 'FREE'
    ? t('nav.historyPersonal')
    : t('nav.history')
  // Le superfici d'istituto richiedono l'appartenenza a una scuola: il DPO
  // cross-tenant (senza scuola) opera dal Cruscotto DPO.
  const isReviewer =
    !!user && ['DIRIGENTE', 'DPO'].includes(user.role) && !!user.school_id

  // Recent assessments, loaded only while the palette is open. Admin,
  // ISPETTORE and DPO never own assessments: skip the query entirely.
  const { data: assessments, isLoading } = useQuery({
    queryKey: ['palette-assessments'],
    queryFn: () => assessmentsApi.list({ page: 1, page_size: 50 }),
    enabled: open && !isAdmin && !isIspettore && !isDpo,
    staleTime: 30_000,
  })

  const run = useCallback((fn: () => void) => {
    setOpen(false)
    fn()
  }, [])

  const handleLogout = async () => {
    try { await authApi.logout() } catch { /* cookie may be gone */ }
    logout()
    navigate('/login')
  }

  const switchLanguage = () => {
    const newLang = language === 'it' ? 'en' : 'it'
    setLanguage(newLang)
    i18n.changeLanguage(newLang)
    localStorage.setItem('varia-lang', newLang)
    toast({ title: newLang === 'it' ? 'Lingua: Italiano' : 'Language: English' })
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] bg-background/70 backdrop-blur-sm flex items-start justify-center pt-[14vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl">
        <Command
          className="rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl overflow-hidden"
          loop
        >
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Command.Input
              autoFocus
              placeholder={t('palette.placeholder')}
              className="flex-1 h-12 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            />
            <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground">
              ESC
            </kbd>
          </div>

          <Command.List className="max-h-[55vh] overflow-y-auto p-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground">
            <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
              {t('palette.noResults')}
            </Command.Empty>

            <Command.Group heading={t('palette.navigation')}>
              <PaletteItem
                icon={<LayoutDashboard />}
                onSelect={() => run(() => navigate(isAdmin ? '/admin' : isDpo ? '/dpo' : '/'))}
                keywords={isAdmin ? 'amministrazione piattaforma' : isDpo ? 'cruscotto dpo dashboard' : undefined}
              >
                {isDpo ? t('nav.dpoDashboard') : t('nav.dashboard')}
              </PaletteItem>
              {!isAdmin && !isIspettore && !isDpo && (
                <>
                  <PaletteItem
                    icon={<PlusCircle />}
                    onSelect={() => run(() => navigate('/assessment/new'))}
                    keywords="nuova valutazione analisi"
                  >
                    {t('nav.newAssessmentCrumb')}
                  </PaletteItem>
                  <PaletteItem icon={<Clock />} onSelect={() => run(() => navigate('/history'))} keywords="cronologia storico ricerca personale">
                    {historyLabel}
                  </PaletteItem>
                </>
              )}
              {isReviewer && (
                <>
                  <PaletteItem icon={<Eye />} onSelect={() => run(() => navigate('/review-queue'))} keywords="validare review approvazione">
                    {t('nav.reviewQueue')}
                  </PaletteItem>
                  <PaletteItem icon={<Shield />} onSelect={() => run(() => navigate('/audit'))} keywords="audit log registro">
                    {t('nav.activityLog')}
                  </PaletteItem>
                  <PaletteItem icon={<Building2 />} onSelect={() => run(() => navigate('/school'))} keywords="istituto scuola membri school">
                    {t('nav.school')}
                  </PaletteItem>
                </>
              )}
              <PaletteItem icon={<User />} onSelect={() => run(() => navigate('/profile'))} keywords="profilo account profile">
                {t('nav.profile')}
              </PaletteItem>
              <PaletteItem icon={<BookOpen />} onSelect={() => run(() => navigate('/guida'))} keywords="guida documentazione aiuto manuale help guide">
                {t('nav.guida')}
              </PaletteItem>
            </Command.Group>

            <Command.Group heading={t('palette.actionsGroup')}>
              <PaletteItem
                icon={document.documentElement.classList.contains('dark') ? <Sun /> : <Moon />}
                onSelect={() => run(toggleDarkMode)}
                keywords="tema scuro chiaro dark light theme"
              >
                {t('palette.toggleTheme')}
              </PaletteItem>
              <PaletteItem icon={<Globe />} onSelect={() => run(switchLanguage)} keywords="lingua italiano inglese language">
                {language === 'it' ? 'Passa a English' : 'Switch to Italiano'}
              </PaletteItem>
              <PaletteItem icon={<LogOut />} onSelect={() => run(handleLogout)} keywords="esci logout sign out">
                {t('nav.logout')}
              </PaletteItem>
            </Command.Group>

            {!isAdmin && !isIspettore && !isDpo && (
            <Command.Group heading={t('palette.recent')}>
              {isLoading && (
                <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}
                </div>
              )}
              {assessments?.map((a) => (
                <PaletteItem
                  key={a.id}
                  icon={a.verdict ? VERDICT_ICONS[a.verdict] : <FileText />}
                  onSelect={() => run(() => navigate(`/assessment/${a.id}`))}
                  keywords={`${a.app.name} ${a.verdict || ''}`}
                >
                  <span className="flex-1 truncate">{a.app.name}</span>
                  {a.verdict && (
                    <span className="text-xs text-muted-foreground ml-2">
                      {verdictLabel(a.verdict, i18n.language)}
                      {a.overall_score ? ` · ${a.overall_score.toFixed(1)}` : ''}
                    </span>
                  )}
                </PaletteItem>
              ))}
            </Command.Group>
            )}
          </Command.List>

          <div className="border-t border-border px-4 py-2 flex items-center gap-4 text-[11px] text-muted-foreground">
            <span><kbd className="px-1 rounded border border-border">↑↓</kbd> {t('palette.navigateHint')}</span>
            <span><kbd className="px-1 rounded border border-border">↵</kbd> {t('palette.openHint')}</span>
            <span className="ml-auto">ValutazIA</span>
          </div>
        </Command>
      </div>
    </div>
  )
}

function PaletteItem({
  icon, children, onSelect, keywords,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onSelect: () => void
  keywords?: string
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      keywords={keywords ? [keywords] : undefined}
      className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm cursor-pointer select-none data-[selected=true]:bg-mim-lightblue data-[selected=true]:text-mim-blue dark:data-[selected=true]:bg-primary/20 dark:data-[selected=true]:text-primary [&_svg]:w-4 [&_svg]:h-4 [&_svg]:flex-shrink-0"
    >
      {icon}
      {children}
    </Command.Item>
  )
}
